import { openDB, type DBSchema, type IDBPDatabase } from "idb";
import type {
  Reading,
  Rebuild,
  RebuildMode,
  DeepReadSession,
  ReadingStore,
} from "./types";

interface SyftDB extends DBSchema {
  readings: { key: string; value: Reading };
  quickReadResults: { key: string; value: unknown };
  rebuilds: { key: [string, RebuildMode]; value: Rebuild };
  deepReadSessions: {
    key: string;
    value: DeepReadSession;
    indexes: { byReadingId: string };
  };
}

const DB_NAME = "syft";
const DB_VERSION = 4;

export const BLOCKED_MESSAGE =
  "Couldn't open the database because another tab has it open from an older version. Please close other Syft tabs and try again.";

// A stale tab can hold the database mid-upgrade without ever firing
// `blocked` (only a connection opened with a `blocking` handler responds to
// the version-change request at all). Without a timeout, a connection like
// that leaves `openSyftDB`'s promise pending forever.
const OPEN_TIMEOUT_MS = 10000;

export class IndexedDbReadingStore implements ReadingStore {
  private dbPromise: Promise<IDBPDatabase<SyftDB>> | null = null;

  private openSyftDB(): Promise<IDBPDatabase<SyftDB>> {
    // openDB's own promise never settles while a `blocked` event is pending
    // (it just waits for the other tab to close its connection), so we wrap
    // it in a promise we control and reject that ourselves the moment
    // `blocked` fires. That turns a silent, indefinite hang into a clear,
    // actionable error for the caller.
    return new Promise<IDBPDatabase<SyftDB>>((resolve, reject) => {
      let settled = false;

      // Some stale connections never respond to the version-change request
      // at all and so never trigger `blocked` above. This timeout catches
      // that case too, with the same message and the same recovery: give up
      // on this attempt so a later call opens a fresh connection.
      const timeoutId = setTimeout(() => {
        if (settled) return;
        settled = true;
        reject(new Error(BLOCKED_MESSAGE));
      }, OPEN_TIMEOUT_MS);

      openDB<SyftDB>(DB_NAME, DB_VERSION, {
        upgrade(db, oldVersion) {
          // Runs against a fresh database or an existing older one, so every
          // store is created only if it is missing.
          if (!db.objectStoreNames.contains("readings")) {
            db.createObjectStore("readings", { keyPath: "id" });
          }
          if (!db.objectStoreNames.contains("quickReadResults")) {
            db.createObjectStore("quickReadResults", { keyPath: "readingId" });
          }
          // A pre-existing database may still carry a deepLookSessions store
          // from v1 Deep Look. It is left in place rather than deleted: an
          // upgrade transaction that does not touch a store leaves it and
          // its data alone, and nothing in this codebase reads or writes it
          // any more (Deep read replaces it entirely with deepReadSessions
          // below), so there is nothing to migrate.
          // The rebuilds store changed shape in v4: keyPath readingId alone
          // becomes a compound key [readingId, mode], so a reading can hold
          // a cached quick and a cached deep rebuild at once. An old-shaped
          // store cannot be migrated in place; it is deleted and recreated,
          // and any previously cached rebuild is lost (one cheap call to
          // regenerate it).
          if (
            oldVersion > 0 &&
            oldVersion < 4 &&
            db.objectStoreNames.contains("rebuilds")
          ) {
            db.deleteObjectStore("rebuilds");
          }
          if (!db.objectStoreNames.contains("rebuilds")) {
            db.createObjectStore("rebuilds", {
              keyPath: ["readingId", "mode"],
            });
          }
          if (!db.objectStoreNames.contains("deepReadSessions")) {
            const sessions = db.createObjectStore("deepReadSessions", {
              keyPath: "id",
            });
            sessions.createIndex("byReadingId", "readingId");
          }
        },
        blocked: () => {
          // We're trying to upgrade here, but a connection opened against the
          // old version is still open elsewhere (e.g. a stale tab) and hasn't
          // responded to `blocking`. Don't let this hang forever — surface a
          // rejection so the caller can show the student something actionable.
          if (settled) return;
          settled = true;
          clearTimeout(timeoutId);
          reject(new Error(BLOCKED_MESSAGE));
        },
        blocking: (_currentVersion, _blockedVersion, event) => {
          // We're the OLD connection here (in whichever tab holds it), and
          // another tab wants to upgrade. Close our connection so that
          // upgrade can proceed there, and drop our memoised promise so the
          // next call in this tab reopens cleanly instead of reusing a
          // now-closed handle.
          (event.target as unknown as IDBDatabase).close();
          this.dbPromise = null;
        },
      })
        .then((db) => {
          if (!settled) {
            settled = true;
            clearTimeout(timeoutId);
            resolve(db);
          } else {
            // We already gave up (the timeout fired) and the caller has
            // moved on. Close this late-arriving connection rather than
            // leak it.
            db.close();
          }
        })
        .catch((err) => {
          if (!settled) {
            settled = true;
            clearTimeout(timeoutId);
            reject(err);
          }
        });
    });
  }

  private getDb(): Promise<IDBPDatabase<SyftDB>> {
    if (!this.dbPromise) {
      this.dbPromise = this.openSyftDB().catch((err) => {
        // Don't leave a rejected promise memoised — a later retry (e.g. once
        // the blocking tab is closed) should get a fresh attempt rather than
        // the same rejection forever.
        this.dbPromise = null;
        throw err;
      });
    }
    return this.dbPromise;
  }

  async addReading(reading: Reading): Promise<void> {
    const db = await this.getDb();
    await db.put("readings", reading);
  }

  async listReadings(): Promise<Reading[]> {
    const db = await this.getDb();
    const all = await db.getAll("readings");
    return all.sort((a, b) => b.createdAt - a.createdAt);
  }

  async getReading(id: string): Promise<Reading | undefined> {
    const db = await this.getDb();
    return db.get("readings", id);
  }

  async saveRebuild(rebuild: Rebuild): Promise<void> {
    const db = await this.getDb();
    await db.put("rebuilds", rebuild);
  }

  async getRebuild(
    readingId: string,
    mode: RebuildMode = "quick",
  ): Promise<Rebuild | undefined> {
    const db = await this.getDb();
    return db.get("rebuilds", [readingId, mode]);
  }

  async saveDeepReadSession(session: DeepReadSession): Promise<void> {
    const db = await this.getDb();
    await db.put("deepReadSessions", session);
  }

  async getDeepReadSession(id: string): Promise<DeepReadSession | undefined> {
    const db = await this.getDb();
    return db.get("deepReadSessions", id);
  }

  async listDeepReadSessionsForReading(
    readingId: string,
  ): Promise<DeepReadSession[]> {
    const db = await this.getDb();
    return db.getAllFromIndex("deepReadSessions", "byReadingId", readingId);
  }

  async renameReading(id: string, title: string): Promise<void> {
    const db = await this.getDb();
    const found = await db.get("readings", id);
    if (found) await db.put("readings", { ...found, title });
  }

  async deleteReading(id: string): Promise<void> {
    const db = await this.getDb();
    // One transaction, so a reading is never left half deleted.
    const tx = db.transaction(
      ["readings", "rebuilds", "deepReadSessions"],
      "readwrite",
    );
    await tx.objectStore("readings").delete(id);
    for (const mode of ["quick", "deep"] as const)
      await tx.objectStore("rebuilds").delete([id, mode]);
    const sessions = await tx
      .objectStore("deepReadSessions")
      .index("byReadingId")
      .getAllKeys(id);
    for (const key of sessions)
      await tx.objectStore("deepReadSessions").delete(key);
    await tx.done;
  }
}
